import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyModule, FormlyFieldConfig } from '@ngx-formly/core';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { takeUntil, catchError, concatMap, tap } from 'rxjs/operators';

import { ConnectorDefinition, Connector } from 'src/app/chatstyle/models/connector.model';
import { Step3Model } from 'src/app/chatstyle/models/onboarding.model';
import { PsaCustomizationService, PsaCustomizationState } from 'src/app/routes/connectors/shared/psa-customization.service';
import { StatusBannerComponent, BannerActionDirective } from '../status-banner/status-banner.component';
import { SectionHeaderComponent } from '../section-header/section-header.component';

@Component({
  selector: 'app-psa-customization-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormlyModule,
    StatusBannerComponent,
    BannerActionDirective,
    SectionHeaderComponent
  ],
  templateUrl: './psa-customization-form.component.html',
  styleUrls: ['./psa-customization-form.component.scss'],
})
export class PsaCustomizationFormComponent implements OnInit, OnDestroy, OnChanges {
  /**
   * The PSA connector to load customization options from
   * Accepts either ConnectorDefinition or Connector (which extends ConnectorDefinition)
   */
  @Input() connector: ConnectorDefinition | Connector | null = null;

  /**
   * PSA ID for determining field requirements and terminology
   */
  @Input() psaId: string = '';

  /**
   * PSA display name for the section header
   */
  @Input() psaName: string = 'PSA';

  /**
   * Initial form model values (for editing existing configuration)
   */
  @Input() initialModel: Step3Model | null = null;

  /**
   * Whether to show a loading state while fetching metadata
   */
  @Input() showLoading: boolean = true;

  /**
   * Whether to show a refresh button
   */
  @Input() showRefreshButton: boolean = true;

  /**
   * When true, all ticket settings fields are optional (no required validation).
   * Used in channel override context where blank = use connector/PSA default.
   */
  @Input() allFieldsOptional: boolean = false;

  /**
   * Custom section title (optional)
   */
  @Input() sectionTitle: string = '';

  /**
   * Custom section subtitle (optional)
   */
  @Input() sectionSubtitle: string = 'Configure default ticket settings for your PSA';

  /**
   * Whether to show time entry settings (Pro+ feature).
   * When false, the time entry section is hidden even for Autotask.
   */
  @Input() showTimeEntrySettings: boolean = true;

  /**
   * Whether to disable the time entry toggle (e.g., for Starter tier).
   * When true, the time entry section is visible but the enable checkbox is disabled.
   */
  @Input() disableTimeEntrySettings: boolean = false;

  /**
   * Emitted when the form model changes
   */
  @Output() modelChange = new EventEmitter<Step3Model>();

  /**
   * Emitted when the form validity changes
   */
  @Output() validityChange = new EventEmitter<boolean>();

  /**
   * Emitted when metadata loading completes (success or error)
   */
  @Output() metadataLoaded = new EventEmitter<{ success: boolean; error?: string }>();

  /**
   * Emitted when the form dirty state changes (user has made changes)
   */
  @Output() formDirtyChange = new EventEmitter<boolean>();

  // Form state
  form: FormGroup = new FormGroup({});
  formFields: FormlyFieldConfig[] = [];
  formModel: Step3Model;

  // Loading state
  isLoadingMetadata = false;
  metadataError = '';

  // Internal state
  private state: PsaCustomizationState;
  private destroy$ = new Subject<void>();
  private initialized = false;
  private metadataLoadComplete = false;
  private formDirty = false;
  private pendingTimeouts: ReturnType<typeof setTimeout>[] = [];

  constructor(
    private psaCustomizationService: PsaCustomizationService
  ) {
    this.state = this.psaCustomizationService.createState();
    this.formModel = this.psaCustomizationService.createEmptyModel();
  }

  ngOnInit(): void {
    this.initializeComponent();
  }

ngOnChanges(changes: SimpleChanges): void {
    // If connector changes after initialization, reload metadata
    if (changes['connector'] && !changes['connector'].firstChange && this.initialized) {
      this.loadMetadata();
    }

    // If initial model changes, update form model
    if (changes['initialModel'] && this.initialModel) {
      this.formModel = { ...this.formModel, ...this.initialModel };
    }
  }

  ngOnDestroy(): void {
    // Clear all pending timeouts
    this.pendingTimeouts.forEach(id => clearTimeout(id));
    this.pendingTimeouts = [];

    this.destroy$.next();
    this.destroy$.complete();
    this.psaCustomizationService.destroyState(this.state);
  }

  private initializeComponent(): void {
    // Initialize form model from input
    if (this.initialModel) {
      this.formModel = { ...this.formModel, ...this.initialModel };
    }

    // Build form fields
    this.buildFormFields();

    // Track form changes - emit dirty after metadata has loaded
    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.modelChange.emit(this.formModel);
        this.validityChange.emit(this.form.valid);

        // Emit dirty change only after metadata load is complete (or after fallback delay)
        if (this.metadataLoadComplete && !this.formDirty) {
          this.formDirty = true;
          this.formDirtyChange.emit(true);
        }
      });

    // Load metadata if connector is provided
    if (this.connector) {
      this.loadMetadata();
    } else {
      // No connector to load metadata from - mark as ready after short delay
      this.pendingTimeouts.push(setTimeout(() => {
        this.metadataLoadComplete = true;
      }, 200));
    }

    // Emit initial validity after a short delay to allow Formly to initialize
    this.pendingTimeouts.push(setTimeout(() => {
      this.validityChange.emit(this.form.valid);
    }, 0));

    this.initialized = true;
  }

  private buildFormFields(): void {
    this.formFields = this.psaCustomizationService.buildFormFields(
      this.state,
      this.psaId,
      this.psaName,
      (boardValue: string) => this.onBoardChange(boardValue),
      this.allFieldsOptional,
      (resourceId: string) => this.onResourceChange(resourceId),
      this.showTimeEntrySettings,
      this.disableTimeEntrySettings
    );

  }

  private onBoardChange(boardValue: string): void {
    this.formModel.defaultStatus = '';
    this.formModel.defaultTicketType = '';
    this.formModel.defaultPriority = '';

    if (this.connector) {
      this.psaCustomizationService
        .loadStatuses(this.connector, this.state, boardValue)
        .pipe(takeUntil(this.destroy$))
        .subscribe();

      this.psaCustomizationService
        .loadTypes(this.connector, this.state, boardValue)
        .pipe(takeUntil(this.destroy$))
        .subscribe();

      this.psaCustomizationService
        .loadPriorities(this.connector, this.state, this.psaId, false, boardValue)
        .pipe(takeUntil(this.destroy$))
        .subscribe();
    }
  }

  private onResourceChange(resourceId: string): void {
    // Clear current role selection when resource changes
    this.formModel.timeEntryDefaultRoleID = '';

    if (!resourceId) {
      this.state.availableRoles$.next([]);
      return;
    }

    // Load roles for the selected resource
    if (this.connector) {
      this.psaCustomizationService
        .loadRoles(this.connector, this.state, resourceId)
        .pipe(takeUntil(this.destroy$))
        .subscribe();
    }
  }

  /**
   * Load PSA metadata (boards, sources, types, priorities)
   */
  public loadMetadata(forceRefresh = false): void {
    if (!this.connector) {
      this.metadataError = 'Connector not available.';
      this.metadataLoaded.emit({ success: false, error: this.metadataError });
      return;
    }

    this.isLoadingMetadata = true;
    this.metadataError = '';

    // Build metadata loading observables (types loaded sequentially after board is known)
    const metadataLoaders: Record<string, Observable<any>> = {
      boards: this.psaCustomizationService.loadBoards(this.connector, this.state, forceRefresh).pipe(catchError(() => of(null))),
      sources: this.psaCustomizationService.loadSources(this.connector, this.state, this.formModel, forceRefresh).pipe(catchError(() => of(null))),
      priorities: this.psaCustomizationService.loadPriorities(this.connector, this.state, this.psaId, forceRefresh).pipe(catchError(() => of(null)))
    };

    // Load resources for Autotask time entry config
    if (this.psaId?.toLowerCase() === 'autotask') {
      metadataLoaders['resources'] = this.psaCustomizationService.loadResources(this.connector, this.state, forceRefresh).pipe(catchError(() => of(null)));
    }

    // Load all metadata in parallel
    forkJoin(metadataLoaders).pipe(
      tap(() => {
        this.isLoadingMetadata = false;
        this.metadataLoaded.emit({ success: true });
      }),
      // If a board was previously selected, load its statuses and types
      concatMap(() => {
        if (this.formModel.defaultBoard && this.connector) {
          return this.psaCustomizationService.loadStatuses(this.connector, this.state, this.formModel.defaultBoard);
        }
        return of(null);
      }),
      concatMap(() => {
        if (this.connector) {
          return this.psaCustomizationService.loadTypes(this.connector, this.state, this.formModel.defaultBoard || undefined);
        }
        return of(null);
      }),
      concatMap(() => {
        if (this.formModel.defaultBoard && this.connector) {
          return this.psaCustomizationService.loadPriorities(this.connector, this.state, this.psaId, false, this.formModel.defaultBoard);
        }
        return of(null);
      }),
      // If a resource was previously selected, load its roles (Autotask time entries)
      concatMap(() => {
        if (this.psaId?.toLowerCase() === 'autotask' && this.formModel.timeEntryDefaultResourceID && this.connector) {
          return this.psaCustomizationService.loadRoles(this.connector, this.state, this.formModel.timeEntryDefaultResourceID);
        }
        return of(null);
      }),
      catchError((error) => {
        this.isLoadingMetadata = false;
        this.metadataError = 'Failed to load PSA configuration. Using defaults.';
        this.metadataLoaded.emit({ success: false, error: this.metadataError });
        return of(null);
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => {
        // Mark metadata load as complete AFTER all loading is done (including statuses)
        // Use delay to let form settle after dropdown options are populated
        if (!this.metadataLoadComplete) {
          this.pendingTimeouts.push(setTimeout(() => {
            this.metadataLoadComplete = true;
          }, 300));
        }
      },
      error: () => {
        // Even on error, mark as complete so user can still interact
        this.pendingTimeouts.push(setTimeout(() => {
          this.metadataLoadComplete = true;
        }, 300));
      }
    });
  }

  /**
   * Reload connector metadata (e.g., after retrying)
   */
  reloadMetadata(): void {
    this.metadataError = '';
    this.loadMetadata(true);
  }

  /**
   * Get the current form model
   */
  getModel(): Step3Model {
    return this.formModel;
  }

  /**
   * Check if the form is valid
   */
  isValid(): boolean {
    return this.form.valid;
  }

  /**
   * Check if the form is dirty (has changes)
   */
  isDirty(): boolean {
    return this.form.dirty || this.formDirty;
  }

  /**
   * Mark the form as pristine (no changes)
   */
  markAsPristine(): void {
    this.form.markAsPristine();
    this.formDirty = false;
  }

  /**
   * Get the effective section title
   */
  getSectionTitle(): string {
    return this.sectionTitle || `${this.psaName} Default Configuration`;
  }

}
